/**
 * useAccountOrders — ViewModel for the /account/orders screen.
 *
 * Per SOP 02 §"MVVM Pattern" + §"List Pagination (LOCKED)" +
 * SOP 15 Ch.20 (SOLID — S/D). Composes the shared usePaginatedList
 * hook so order history is page-able end-to-end (real customers will
 * accumulate many orders; SOP 02 mandates pagination on every list).
 */

import { useCallback } from 'react'
import { useAuth } from '../../context/AuthContext'
import { APIROUTES, fillPath } from '../../constants/routes.constants'
import { mutateJson } from '../../utils/api/mutateJson'
import type { Order } from '../../models/order.model'
import { parseApiError } from '../../utils/api/parseApiError'
import { usePaginatedList } from '../usePaginatedList'

interface ListBody {
  readonly success: true
  readonly data: {
    readonly items: Order[]
    readonly total: number
    readonly page: number
    readonly pageSize: number
  }
}

export interface UseAccountOrdersReturn {
  readonly orders: readonly Order[]
  readonly total: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly error: string | null
  readonly setPage: (n: number) => void
  readonly reload: () => void
  /** Customer Return button on the list card — POST
   *  /account/orders/:id/return with the reason. Same wire as the
   *  detail page's `fileReturn`. */
  readonly fileReturn: (
    orderId: string,
    reason: string
  ) => Promise<{ ok: true } | { ok: false; message: string }>
  /** Customer Cancel-return button on the list card — DELETE
   *  /account/orders/:id/return. Soft-deletes the return + reverts
   *  order.status to 'delivered'. Kiran 2026-06-18. */
  readonly cancelReturn: (
    orderId: string
  ) => Promise<{ ok: true } | { ok: false; message: string }>
  /** Customer Cancel-order button on the list card — POST
   *  /account/orders/:id/cancel. Flips status to 'cancelled' (terminal)
   *  while the order is still cancellable. Kiran 2026-06-19. */
  readonly cancelOrder: (
    orderId: string
  ) => Promise<{ ok: true } | { ok: false; message: string }>
}

export function useAccountOrders(): UseAccountOrdersReturn {
  const { authedFetch } = useAuth()

  const paged = usePaginatedList<Order, undefined>({
    fetcher: useCallback(
      async (page, pageSize, signal) => {
        const url = `${APIROUTES.ORDERS}?page=${page}&pageSize=${pageSize}`
        const res = await authedFetch(url, { signal })
        if (!res.ok) {
          throw new Error((await parseApiError(res)).message)
        }
        const body = (await res.json()) as ListBody
        return { items: body.data.items, total: body.data.total }
      },
      [authedFetch]
    )
  })

  const fileReturn = useCallback(
    async (orderId: string, reason: string) => {
      const r = await mutateJson<{ return_id: string }>(
        authedFetch,
        fillPath(APIROUTES.ACCOUNTORDERRETURN, { id: orderId }),
        'POST',
        { reason }
      )
      if (r.ok) {
        // Status transition flows through ORDER_STATUS_QUEUE — wait ~1s
        // for the consumer to land the history row before reloading the
        // list so the badge + returnable flag refresh together.
        await new Promise((resolve) => setTimeout(resolve, 1000))
        paged.reload()
        return { ok: true as const }
      }
      return { ok: false as const, message: r.message }
    },
    [authedFetch, paged]
  )

  const cancelReturn = useCallback(
    async (orderId: string) => {
      const r = await mutateJson<{ status: string }>(
        authedFetch,
        fillPath(APIROUTES.ACCOUNTORDERRETURN, { id: orderId }),
        'DELETE'
      )
      if (r.ok) {
        await new Promise((resolve) => setTimeout(resolve, 1000))
        paged.reload()
        return { ok: true as const }
      }
      return { ok: false as const, message: r.message }
    },
    [authedFetch, paged]
  )

  const cancelOrder = useCallback(
    async (orderId: string) => {
      const r = await mutateJson<{ status: string }>(
        authedFetch,
        fillPath(APIROUTES.ACCOUNTORDERCANCEL, { id: orderId }),
        'POST'
      )
      if (r.ok) {
        await new Promise((resolve) => setTimeout(resolve, 1000))
        paged.reload()
        return { ok: true as const }
      }
      return { ok: false as const, message: r.message }
    },
    [authedFetch, paged]
  )

  return {
    orders: paged.items,
    total: paged.total,
    page: paged.page,
    pageSize: paged.pageSize,
    isLoading: paged.isLoading,
    error: paged.error,
    setPage: paged.setPage,
    reload: paged.reload,
    fileReturn,
    cancelReturn,
    cancelOrder
  }
}
